import { FC, useCallback } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Text, IconButton, SwitchField, useControlledField, SpinningLoader, useTooltip } from '@frontend/design-system';
import { Attachment } from '../../../../types/form-packet-selector.types';
import {
  getContainerStyle,
  getESignContainerStyle,
  getFileNameStyle,
  getInnerContainerStyle,
  removeActionContainerStyle,
} from './accepted-file.styles';

export interface AcceptedFileInheritedProps {
  onToggleSignatureRequirement: (attachmentId: string, eSignRequired: boolean) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onAbortFileUpload: (attachmentId: string) => void;
  removeAttachmentTrackingId?: string;
  toggleSignatureRequirementTrackingId?: string;
  abortFileUploadTrackingId?: string;
  isEnhancedFlowEnabled?: boolean;
}

interface AcceptedFileProps extends AcceptedFileInheritedProps {
  attachment: Attachment;
}

const AcceptedFile: FC<AcceptedFileProps> = ({
  attachment,
  onToggleSignatureRequirement,
  onAbortFileUpload,
  onRemoveAttachment,
  removeAttachmentTrackingId,
  toggleSignatureRequirementTrackingId,
  abortFileUploadTrackingId,
  isEnhancedFlowEnabled = false,
}) => {
  const { t } = useTranslation('forms');
  const { id, file, eSignRequired, isUploading, hasUploadError } = attachment;
  const { name } = file;

  const {
    Tooltip: FileNameTooltip,
    tooltipProps: fileNameTooltipProps,
    triggerProps: fileNameTooltipTriggerProps,
  } = useTooltip();

  const switchFieldProps = useControlledField({
    type: 'switch',
    value: eSignRequired,
    onChange: (newValue: boolean) => {
      onToggleSignatureRequirement(id, newValue);
    },
  });

  const removeAttachmentHandler = useCallback(() => {
    onRemoveAttachment(id);
  }, [onRemoveAttachment, id]);

  const abortFileHandler = useCallback(() => {
    onAbortFileUpload(id);
  }, [onAbortFileUpload, id]);

  function getButtons() {
    if (isUploading) {
      return null;
    }

    return (
      <>
        {!hasUploadError && (
          <SwitchField
            name=''
            label={t('Patient’s e-sign required')}
            {...switchFieldProps}
            labelPlacement='left'
            trackingId={toggleSignatureRequirementTrackingId}
          />
        )}

        <div css={isEnhancedFlowEnabled ? removeActionContainerStyle : undefined}>
          <IconButton label='' onClick={removeAttachmentHandler} trackingId={removeAttachmentTrackingId}>
            <Icon name='trash' />
          </IconButton>
        </div>
      </>
    );
  }

  return (
    <div css={getContainerStyle({ hasError: hasUploadError, isEnhancedFlowEnabled })}>
      <div css={getInnerContainerStyle({ isEnhancedFlowEnabled })}>
        {isUploading ? (
          <SpinningLoader size='small' />
        ) : (
          <Icon name={isEnhancedFlowEnabled ? 'document' : 'attachment'} size={18} />
        )}
        <Text {...fileNameTooltipTriggerProps} css={getFileNameStyle({ truncate: !hasUploadError })}>
          {name}
        </Text>
        {isUploading && (
          <IconButton label='' onClick={abortFileHandler} trackingId={abortFileUploadTrackingId}>
            <Icon name='x-small' />
          </IconButton>
        )}
        <FileNameTooltip {...fileNameTooltipProps}>{name}</FileNameTooltip>
      </div>
      <div css={getESignContainerStyle({ isEnhancedFlowEnabled })}>{getButtons()}</div>
    </div>
  );
};

export default AcceptedFile;
